import {
    AlertTriangle,
    Building,
    Check,
    CheckCircle,
    Info,
    Lightbulb,
    Minus,
    Plus,
    TrendingUp,
    X
} from 'lucide-react';
import { useState } from 'react';
import { strategiesData } from './strategiesData';

const InvestmentStrategiesSelector = ({ 
    // Données reçues du parent
    selectedStrategies = [],
    loading = false,
    error = null,
    saving = false,
    
    // Handlers pour les modifications
    onStrategiesChange
}) => {
    // États locaux pour l'UI seulement
    const [selectedStrategy, setSelectedStrategy] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState('investment');

    // Convertir les données en tableau et ajouter les fonctions utilitaires
    const availableStrategies = Object.values(strategiesData);

    // Fonction de tri par niveau de complexité
    const sortByComplexity = (strategies) => {
        const levelOrder = { 'Débutant': 1, 'Intermédiaire': 2, 'Expert': 3 };
        return [...strategies].sort((a, b) => levelOrder[a.level] - levelOrder[b.level]);
    };

    // Catégoriser et trier les stratégies
    const investmentStrategies = sortByComplexity(
        availableStrategies.filter(strategy =>
            ['location-longue-duree', 'location-courte-duree', 'colocation', 'sous-location'].includes(strategy.id)
        )
    );

    const entrepreneurialStrategies = sortByComplexity(
        availableStrategies.filter(strategy =>
            ['achat-revente', 'renovation-lourde', 'division-creation-lot', 'vente-a-la-decoupe', 'changement-destination'].includes(strategy.id)
        )
    );

    const currentStrategies = activeTab === 'investment' ? investmentStrategies : entrepreneurialStrategies;

    const getRiskColor = (risk, isSelected = false) => {
        const colors = {
            'Faible': isSelected ? 'bg-green-200 text-green-800' : 'bg-green-100 text-green-700',
            'Moyen': isSelected ? 'bg-yellow-200 text-yellow-800' : 'bg-yellow-100 text-yellow-700',
            'Élevé': isSelected ? 'bg-orange-200 text-orange-800' : 'bg-orange-100 text-orange-700',
            'Très élevé': isSelected ? 'bg-red-200 text-red-800' : 'bg-red-100 text-red-700'
        };
        return colors[risk] || 'bg-gray-100 text-gray-600';
    };

    const getLevelColor = (level) => {
        const colors = {
            'Débutant': 'bg-blue-100 text-blue-700',
            'Intermédiaire': 'bg-purple-100 text-purple-700',
            'Expert': 'bg-red-100 text-red-700'
        };
        return colors[level] || 'bg-gray-100 text-gray-600';
    };

    const handleStrategyToggle = (strategyId) => {
        let newStrategies;

        if (selectedStrategies.includes(strategyId)) {
            newStrategies = selectedStrategies.filter(id => id !== strategyId);
        } else {
            newStrategies = [...selectedStrategies, strategyId];
        }

        if (onStrategiesChange) {
            onStrategiesChange(newStrategies);
        }
    };

    const openStrategyModal = (strategy, event) => {
        event.stopPropagation();
        setSelectedStrategy(strategy);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedStrategy(null);
    };

    // Affichage du loading
    if (loading) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-gray-600 text-sm">Chargement des stratégies...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full flex flex-col shadow-sm">
                {/* Header */}
                <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center text-slate-700">
                            <h2 className="text-sm font-semibold">Stratégies d'Investissement ou d'entrepreneuriat</h2>
                        </div>
                        <div className="flex items-center space-x-2">
                            {saving && (
                                <span className="text-xs text-green-600 font-medium">
                                    Sauvegarde...
                                </span>
                            )}
                            <div className="bg-gray-200 text-gray-700 px-2 py-1 rounded-lg text-xs font-medium">
                                {selectedStrategies.length} sélectionnées
                            </div>
                        </div>
                    </div>
                </div>

                {/* Affichage d'erreur */}
                {error && (
                    <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center">
                            <div className="w-4 h-4 rounded-full bg-red-500 mr-2 flex-shrink-0"></div>
                            <span className="text-sm text-red-800">{error}</span>
                        </div>
                    </div>
                )}

                {/* Onglets séparés */}
                <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
                    <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                        <button
                            onClick={() => setActiveTab('investment')}
                            className={`flex items-center px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${activeTab === 'investment'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            <TrendingUp className="w-3 h-3 mr-1.5" />
                            Investissement
                        </button>
                        <button
                            onClick={() => setActiveTab('entrepreneurial')}
                            className={`flex items-center px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${activeTab === 'entrepreneurial'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            <Building className="w-3 h-3 mr-1.5" />
                            Entrepreneuriat
                        </button>
                    </div>
                </div>

                {/* Contenu principal */}
                <div className="flex-1 overflow-y-auto p-4">
                    <div className="grid grid-cols-2 gap-2 mb-4">
                        {currentStrategies.map((strategy) => {
                            const isSelected = selectedStrategies.includes(strategy.id);

                            return (
                                <button
                                    key={strategy.id}
                                    onClick={() => handleStrategyToggle(strategy.id)}
                                    className={`
                                        relative p-2 rounded-lg border transition-all duration-200 text-left cursor-pointer hover:scale-[1.02]
                                        ${isSelected
                                            ? 'border-[#0e9488] bg-[#0e9488]/10 shadow-sm'
                                            : 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-25'
                                        }
                                    `}
                                >
                                    <div className="flex items-start justify-between mb-1">
                                        <div className="flex items-center">
                                            {(() => {
                                                const IconComponent = strategy.icon;
                                                return IconComponent ? <IconComponent className={`w-4 h-4 mr-2 ${isSelected ? 'text-gray-600' : 'text-slate-500'}`} /> : null;
                                            })()}
                                            <h3 className={`font-semibold text-xs ${isSelected ? 'text-slate-900' : 'text-slate-800'}`}>
                                                {strategy.name}
                                            </h3>
                                        </div>
                                        {isSelected && (
                                            <div className="w-3 h-3 bg-gray-500 rounded-full flex items-center justify-center">
                                                <Check className="w-2 h-2 text-white" />
                                            </div>
                                        )}
                                    </div>

                                    <p className={`text-xs mb-2 line-clamp-2 ${isSelected ? 'text-slate-700' : 'text-slate-600'}`}>
                                        {strategy.description}
                                    </p>

                                    <div className="flex justify-between items-end text-xs gap-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-xs ${isSelected ? 'text-slate-700' : 'text-slate-600'}`}>
                                                Durée : {strategy.duration}
                                            </span>
                                            <span className={`text-xs ${isSelected ? 'text-slate-700' : 'text-slate-600'}`}>
                                                Niveau : {strategy.level}
                                            </span>
                                            <span className={`text-xs ${isSelected ? 'text-slate-700' : 'text-slate-600'}`}>
                                                Risque : {strategy.risk}
                                            </span>
                                        </div>
                                        {/* Bouton d'information */}
                                        <button
                                            onClick={(e) => openStrategyModal(strategy, e)}
                                            className="w-5 h-5 text-slate-700 rounded-full flex items-center justify-center hover:bg-blue-100 transition-all duration-200 hover:scale-110 shadow-sm font-bold text-xs flex-shrink-0"
                                            style={{ backgroundColor: '#d2e3ff' }}
                                            title="Voir les détails de cette stratégie"
                                        >
                                            ?
                                        </button>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Modal de détails de stratégie */}
            {showModal && selectedStrategy && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
                        {/* Header du modal */}
                        <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">{selectedStrategy.name}</h2>
                                    <p className="text-sm text-gray-600 mt-1">{selectedStrategy.description}</p>
                                </div>
                                <button
                                    onClick={closeModal}
                                    className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
                                >
                                    <X className="w-4 h-4 text-gray-500" />
                                </button>
                            </div>
                        </div>

                        {/* Contenu du modal */}
                        <div className="p-6">
                            {/* Informations générales */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                                <div className="text-center p-3 bg-gray-50 rounded-md border border-gray-100">
                                    <div className="text-xl font-semibold text-gray-800">{selectedStrategy.roi}</div>
                                    <div className="text-xs text-gray-600 mt-1">ROI attendu</div>
                                </div>
                                <div className="text-center p-3 bg-gray-50 rounded-md border border-gray-100">
                                    <div className="text-xl font-semibold text-gray-800">{selectedStrategy.timeframe || selectedStrategy.duration}</div>
                                    <div className="text-xs text-gray-600 mt-1">Durée</div>
                                </div>
                                <div className="text-center p-3 bg-gray-50 rounded-md border border-gray-100">
                                    <div className="text-xs px-2 py-1 rounded-md font-medium bg-gray-200 text-gray-700">
                                        {selectedStrategy.level}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">Niveau</div>
                                </div>
                                <div className="text-center p-3 bg-gray-50 rounded-md border border-gray-100">
                                    <div className="text-xs px-2 py-1 rounded-md font-medium bg-gray-200 text-gray-700">
                                        {selectedStrategy.risk}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">Risque</div>
                                </div>
                            </div>

                            {/* Sections détaillées */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Avantages */}
                                <div>
                                    <h3 className="flex items-center text-sm font-medium text-gray-800 mb-3">
                                        <Plus className="w-3.5 h-3.5 text-gray-500 mr-2" />
                                        Avantages
                                    </h3>
                                    <div className="space-y-2">
                                        {selectedStrategy.advantages?.map((advantage, index) => (
                                            <div key={index} className="flex items-start">
                                                <div className="w-1 h-1 rounded-full bg-gray-400 mr-3 mt-2 flex-shrink-0"></div>
                                                <span className="text-sm text-gray-700">{advantage}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Inconvénients */}
                                <div>
                                    <h3 className="flex items-center text-sm font-medium text-gray-800 mb-3">
                                        <Minus className="w-3.5 h-3.5 text-gray-500 mr-2" />
                                        Inconvénients
                                    </h3>
                                    <div className="space-y-2">
                                        {selectedStrategy.disadvantages?.map((disadvantage, index) => (
                                            <div key={index} className="flex items-start">
                                                <div className="w-1 h-1 rounded-full bg-gray-400 mr-3 mt-2 flex-shrink-0"></div>
                                                <span className="text-sm text-gray-700">{disadvantage}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Astuces */}
                                <div>
                                    <h3 className="flex items-center text-sm font-medium text-gray-800 mb-3">
                                        <Lightbulb className="w-3.5 h-3.5 text-gray-500 mr-2" />
                                        Astuces & Conseils
                                    </h3>
                                    <div className="space-y-2">
                                        {selectedStrategy.tips?.map((tip, index) => (
                                            <div key={index} className="flex items-start">
                                                <div className="w-1 h-1 rounded-full bg-gray-400 mr-3 mt-2 flex-shrink-0"></div>
                                                <span className="text-sm text-gray-700">{tip}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Prérequis */}
                                <div>
                                    <h3 className="flex items-center text-sm font-medium text-gray-800 mb-3">
                                        <Info className="w-3.5 h-3.5 text-gray-500 mr-2" />
                                        Prérequis
                                    </h3>
                                    <div className="space-y-2">
                                        {selectedStrategy.requirements?.map((requirement, index) => (
                                            <div key={index} className="flex items-start">
                                                <div className="w-1 h-1 rounded-full bg-gray-400 mr-3 mt-2 flex-shrink-0"></div>
                                                <span className="text-sm text-gray-700">{requirement}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer du modal */}
                        <div className="px-6 py-4 bg-gray-100 border-t border-gray-200">
                            <div className="flex justify-end">
                                <button
                                    onClick={closeModal}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors font-medium text-sm"
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default InvestmentStrategiesSelector;